import { freshnessDate, reviewLabelDE } from "@/lib/currentYear";
import { useEffect, useState } from "react";
import { useParams, Navigate, useNavigate } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { SEOHelmet } from "@/components/SEOHelmet";
import { generateBreadcrumbSchema, generateFAQSchema } from "@/lib/schemaGenerator";
import { standaloneArticleJsonLd } from "@/lib/schema-articles";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User, Share2 } from "lucide-react";
import { formatGermanDate } from "@/lib/dateFormat";
import { ArticleCard } from "@/components/ArticleCard";
import { SectionRenderer } from "@/components/SectionRenderer";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { GlossaryView } from "@/components/GlossaryView";
import { SponsoredCardsModule } from "@/components/SponsoredCardsModule";
import '@/styles/lexical-editor.css';
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "@/lib/router-compat";

interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string;
  featured_image_url: string | null;
  og_image_url: string | null;
  author: string | null;
  published_at: string | null;
  updated_at: string | null;
  reading_time_minutes: number | null;
  meta_title: string | null;
  meta_description: string | null;
  view_count: number | null;
  custom_scripts: string | null;
  category_id?: string | null;
  article_categories?: Array<{
    article_categories: {
      id: string;
      name: string;
      slug: string;
    };
  }>;
}

const processArticleBody = (body: string): string => {
  // Check for regular HTML comment placeholder
  if (body.includes('<!-- CUSTOM_SCRIPTS -->')) {
    return body.replace(
      '<!-- CUSTOM_SCRIPTS -->',
      '<div id="custom-scripts-container"></div>'
    );
  }
  
  // Handle HTML-encoded version that may come from rich text editor
  // This regex matches the encoded placeholder even if wrapped in HTML tags
  if (body.includes('&lt;!-- CUSTOM_SCRIPTS --&gt;')) {
    return body.replace(
      /(<[^>]*>)*&lt;!-- CUSTOM_SCRIPTS --&gt;(<\/[^>]*>)*/g,
      '<div id="custom-scripts-container"></div>'
    );
  }
  
  return body;
};

const ArticleDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: settings } = useSiteSettings();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [articleSchemas, setArticleSchemas] = useState<any>({ article: null, breadcrumb: null });
  const [faqSchema, setFaqSchema] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  useEffect(() => {
    if (article) generateArticleSchemas();
  }, [article]);

  // Inject custom scripts when article is loaded
  useEffect(() => {
    if (article?.custom_scripts) {
      const customScriptsHtml = article.custom_scripts;
      // Wait for DOM to be ready
      const timer = setTimeout(() => {
        const targetContainer = document.getElementById('custom-scripts-container');
        
        // Only inject if container exists (article has placeholder)
        if (targetContainer) {
          const container = document.createElement('div');
          container.innerHTML = customScriptsHtml;
          
          const scripts = container.querySelectorAll('script');
          const addedScripts: HTMLScriptElement[] = [];
          
          scripts.forEach((oldScript) => {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });
            newScript.textContent = oldScript.textContent;
            targetContainer.appendChild(newScript);
            addedScripts.push(newScript);
          });
          
          // Store for cleanup
          (window as any).__articleScripts = addedScripts;
        }
      }, 100); // Small delay to ensure DOM is ready

      return () => {
        clearTimeout(timer);
        // Cleanup scripts if they exist
        if ((window as any).__articleScripts) {
          (window as any).__articleScripts.forEach((script: HTMLScriptElement) => script.remove());
          delete (window as any).__articleScripts;
        }
        
        const targetContainer = document.getElementById('custom-scripts-container');
        if (targetContainer) {
          targetContainer.innerHTML = '';
        }
      };
    }
    return undefined;
  }, [article?.custom_scripts]);

  const parseFAQsFromBody = (html: string): Array<{question: string, answer: string}> => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    
    const faqs: Array<{question: string, answer: string}> = [];
    
    // Method 1: Look for explicit FAQ elements with data attributes
    const faqElements = doc.querySelectorAll('[data-faq-question], .faq-item, .accordion-item');
    faqElements.forEach(el => {
      const question = el.getAttribute('data-faq-question') || 
                       el.querySelector('.faq-question, .accordion-trigger, h3, h4')?.textContent?.trim();
      const answer = el.getAttribute('data-faq-answer') || 
                     el.querySelector('.faq-answer, .accordion-content, p')?.textContent?.trim();
      if (question && answer) {
        faqs.push({ question, answer });
      }
    });
    
    // Method 2: Look for FAQ structured as heading + paragraph pairs (questions end with ?)
    if (faqs.length === 0) {
      const headings = doc.querySelectorAll('h3, h4');
      headings.forEach(heading => {
        const question = heading.textContent?.trim();
        if (question && question.includes('?')) {
          const nextEl = heading.nextElementSibling;
          if (nextEl?.tagName === 'P') {
            const answer = nextEl.textContent?.trim();
            if (answer) {
              faqs.push({ question, answer });
            }
          }
        }
      });
    }
    
    return faqs;
  };

  // Helper to apply SEO template replacements
  const applyTemplate = (template: string, replacements: Record<string, string>) => {
    return Object.entries(replacements).reduce(
      (result, [key, value]) => result.replace(new RegExp(`\\{${key}\\}`, 'g'), value),
      template
    );
  };

  const generateArticleSchemas = async () => {
    if (!article || !settings) return;
    
    const defaultImage = article.featured_image_url || settings.default_og_image;
    const category = (article as any).article_categories ? {
      name: (article as any).article_categories.name,
      slug: (article as any).article_categories.slug
    } : undefined;
    
    const articleUrl = `${settings.domain}/ratgeber/${article.slug}`;
    
    // Calculate word count from article body
    const stripHtml = (html: string): string => {
      return html.replace(/<[^>]*>/g, '');
    };
    const plainText = stripHtml(article.body);
    const wordCount = plainText.split(/\s+/).filter(Boolean).length;
    
    // Extract keywords from article and category
    const keywords = [
      "Kreditkarte",
      ...(category ? [category.name] : []),
      "Zinsen",
      "Gebühren",
      "Finanzen",
      "Vergleich"
    ].filter(Boolean).slice(0, 6);
    
    const articleSchema = standaloneArticleJsonLd({
      url: articleUrl,
      headline: article.title,
      description: article.meta_description || article.excerpt || "",
      image: defaultImage,
      authorName: article.author || settings.default_author || "Redaktion",
      datePublished: article.published_at || new Date().toISOString(),
      dateModified: freshnessDate(article.updated_at),
      breadcrumbs: [
        { name: settings.breadcrumb_translations?.home || 'Startseite', url: settings.domain },
        { name: category?.name || settings.breadcrumb_translations?.articles || 'Ratgeber', url: `${settings.domain}/ratgeber` },
        { name: article.title, url: articleUrl }
      ],
      organizationName: settings.organization_name || 'Kreditkarten Vergleich',
      organizationLogo: settings.site_logo_url
    });
    
    const breadcrumbSchema = generateBreadcrumbSchema([
      { name: settings.breadcrumb_translations?.home || 'Startseite', url: settings.domain },
      { name: category?.name || settings.breadcrumb_translations?.articles || 'Ratgeber', url: `${settings.domain}/ratgeber` },
      { name: article.title, url: articleUrl }
    ]);
    
    // Extract and generate FAQ schema if present
    const faqs = parseFAQsFromBody(article.body);
    if (faqs.length > 0) {
      const faqSchemaData = await generateFAQSchema(faqs);
      setFaqSchema(faqSchemaData);
    } else {
      setFaqSchema(null);
    }
    
    setArticleSchemas({ article: articleSchema, breadcrumb: breadcrumbSchema });
  };

  const fetchArticle = async () => {
    try {
      // Fetch article without inner join to avoid filtering out articles without categories
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .eq("show_on_homepage", false)
        .maybeSingle();

      if (error || !data) {
        setNotFound(true);
        return;
      }

      // If article has a category, fetch category details separately
      let articleWithCategory = data as any;
      if (data.category_id) {
        const { data: category } = await supabase
          .from("article_categories")
          .select("id, name, slug")
          .eq("id", data.category_id)
          .maybeSingle();
        
        if (category) {
          articleWithCategory.article_categories = category;
        }
      }

      setArticle(articleWithCategory);

      // Increment view count
      if (data.id) {
        supabase
          .from("articles")
          .update({ view_count: (data.view_count || 0) + 1 })
          .eq("id", data.id)
          .then();
      }

      // Fetch related articles
      if (data.category_id) {
        const { data: related } = await supabase
          .from("articles")
          .select(`
            id,
            slug,
            title,
            excerpt,
            featured_image_url,
            author,
            published_at,
            reading_time_minutes,
            article_categories (
              name
            )
          `)
          .eq("status", "published")
          .eq("exclude_from_listings", false)
          .eq("category_id", data.category_id)
          .neq("id", data.id)
          .limit(3);

        setRelatedArticles(related || []);
      }
    } catch (error) {
      console.error("Error fetching article:", error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = `${settings?.domain}/${article?.slug}`;
    const shareTitle = article?.title;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
        toast({
          title: "Delt!",
          description: "Artikkelen er delt",
        });
      } catch (error) {
        console.log("Share failed:", error);
      }
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Lenke kopiert",
        description: "Lenken til artikkelen er kopiert til utklippstavlen",
      });
    }
  };

  // Dynamic OG image URL with cache-busting
  const dynamicOgImage = article 
    ? `${settings?.domain}/functions/v1/generate-og-image-v2?type=article&slug=${article.slug}&v=${article.updated_at ? new Date(article.updated_at).getTime() : Date.now()}` 
    : undefined;
  const additionalSchemas = [articleSchemas.breadcrumb, faqSchema].filter(Boolean);

  const parseGlossaryTerms = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const termElements = doc.querySelectorAll(".glossary-term");

    return Array.from(termElements).map((element) => {
      const term = element.getAttribute("data-term") || "";
      const synonymsAttr = element.getAttribute("data-synonyms") || "";
      const synonyms = synonymsAttr ? synonymsAttr.split(",").filter(Boolean) : [];
      const definitionEl = element.querySelector(".glossary-definition");
      const definition = definitionEl?.innerHTML || "";

      return { term, definition, synonyms };
    });
  };

  const parseGlossaryIntro = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const introEl = doc.querySelector(".glossary-intro");
    return introEl?.innerHTML || "";
  };

  const handleNavigate = (page: string) => {
    if (page === 'home') {
      navigate('/');
    }
  };

  const isGlossary = article?.slug === "ordliste";
  const glossaryTerms = isGlossary && article ? parseGlossaryTerms(article.body) : [];
  const glossaryIntro = isGlossary && article ? parseGlossaryIntro(article.body) : "";

  if (loading) {
    return (
      <>
        <Header onNavigate={handleNavigate} currentPage="article" />
        <div className="container mx-auto px-4 py-8">
          <div className="mx-auto max-w-4xl">
            <div className="h-12 w-3/4 animate-pulse rounded-sm bg-muted mb-4" />
            <div className="h-6 w-1/2 animate-pulse rounded-sm bg-muted mb-8" />
            <div className="aspect-video w-full animate-pulse rounded-sm bg-muted mb-8" />
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-4 w-full animate-pulse rounded-sm bg-muted" />
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (notFound || !article) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHelmet
        title={
          article.meta_title || 
          (settings?.seo_templates?.article_detail.title_template 
            ? applyTemplate(settings.seo_templates.article_detail.title_template, { title: article.title }) 
            : article.title)
        }
        description={
          article.meta_description || 
          article.excerpt || 
          (settings?.seo_templates?.article_detail.description_template 
            ? applyTemplate(settings.seo_templates.article_detail.description_template, { 
                excerpt: article.excerpt || article.title,
                title: article.title 
              }) 
            : "")
        }
        type="article"
        url={`${settings?.domain || ''}/ratgeber/${article.slug}`}
        image={dynamicOgImage || article.og_image_url || article.featured_image_url || undefined}
        structuredData={articleSchemas.article}
        additionalSchemas={additionalSchemas}
        author={article.author || undefined}
        publishedTime={article.published_at || undefined}
        modifiedTime={freshnessDate(article.updated_at)}
      />

      <Header onNavigate={handleNavigate} currentPage="article" />

      {/* Breadcrumb Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-4 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb className="mb-0">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">{settings?.breadcrumb_translations?.home || 'Startseite'}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/ratgeber">{settings?.breadcrumb_translations?.articles || 'Ratgeber'}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{article.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <article>
            {/* Category Badge */}
            {article.article_categories && article.article_categories.length > 0 && (
              <div className="flex gap-2 mb-4">
                {article.article_categories.map((cat) => (
                  <Badge key={cat.article_categories.id} variant="secondary">
                    {cat.article_categories.name}
                  </Badge>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="mb-6 text-4xl font-bold md:text-5xl">
              {settings?.seo_templates?.article_detail.h1_template 
                ? applyTemplate(settings.seo_templates.article_detail.h1_template, { title: article.title }) 
                : article.title}
            </h1>

          {/* Meta Information */}
          <div className="mb-8 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {article.author && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Von {article.author}</span>
              </div>
            )}
            {article.published_at && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {formatGermanDate(article.published_at, 'd. MMMM yyyy')}
                  <span className="text-muted-foreground">
                    {' · zuletzt geprüft: '}{reviewLabelDE()}
                  </span>
                </span>
              </div>
            )}
            {article.reading_time_minutes && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Lesezeit: {article.reading_time_minutes} {article.reading_time_minutes === 1 ? 'Minute' : 'Minuten'}</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              className="ml-auto"
            >
              <Share2 className="mr-2 h-4 w-4" />
              Teilen
            </Button>
          </div>

            {/* Featured Image */}
            {article.featured_image_url && (
              <div className="mb-8 overflow-hidden rounded-lg">
                <img
                  src={article.featured_image_url}
                  alt={article.title}
                  className="w-full"
                />
              </div>
            )}

            {/* Article Body */}
            {isGlossary ? (
              <div className="space-y-8">
                {glossaryIntro && (
                  <div 
                    className="prose prose-lg max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: glossaryIntro }}
                  />
                )}
                <GlossaryView terms={glossaryTerms} />
              </div>
            ) : (
              <div 
                className="prose prose-lg max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: processArticleBody(article.body) }}
              />
            )}

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-16">
                <h2 className="mb-6 text-2xl font-bold">Ähnliche Artikel</h2>
                <div className="grid gap-6 md:grid-cols-2">
                  {relatedArticles.map((related: any) => (
                    <ArticleCard
                      key={related.id}
                      slug={related.slug}
                      title={related.title}
                      excerpt={related.excerpt}
                      featuredImage={related.featured_image_url}
                      categoryName={related.article_categories?.name}
                      author={related.author}
                      publishedAt={related.published_at}
                      readingTime={related.reading_time_minutes}
                    />
                  ))}
                </div>
              </div>
            )}
          </article>

          <aside className="lg:sticky lg:top-24">
            <SponsoredCardsModule 
              articleId={article.id}
              categoryId={article.category_id || undefined}
            />
          </aside>
        </div>
      </div>

      {/* Dynamic Sections */}
      <SectionRenderer
        currentPage="article-detail"
        onViewDetails={(slug) => navigate(`/kort/${slug}`)}
        onViewAllCards={() => navigate("/")}
      />

      <Footer onNavigate={handleNavigate} />
    </div>
  );
};

export default ArticleDetail;
